import React, { useState, useEffect } from 'react'
import {
  Card, Button, Tag, Tabs, Table, Modal, Form, Input, Empty,
  Select, Alert, Descriptions, Divider, message, Space, Row, Col,
  Tooltip, Skeleton,
} from 'antd'
import {
  ArrowLeftOutlined, EditOutlined, MailOutlined, PrinterOutlined,
  FilePdfOutlined, CheckCircleOutlined, CloseCircleOutlined,
  ExclamationCircleOutlined, FileTextOutlined, CopyOutlined,
  DollarOutlined,
} from '@ant-design/icons'
import { useNavigate, useParams } from 'react-router-dom'
import { downloadInvoiceAsPDF, generateInvoiceHTML, printInvoicePDF, emailInvoice, stripAllocationRef } from '@/utils/invoicePdfGenerator'
import { printReceipt, downloadReceiptAsPDF } from '@/utils/receiptGenerator'
import { invoiceStore } from './invoiceStore'
import { paymentStore } from '@/pages/Payment/paymentStore'
import { useMasterData } from '@/context/MasterDataContext'
import { useModulePermission } from '@/hooks/useModulePermission'
import { WorkflowChainDisplay } from '@/components/WorkflowChainDisplay'
import { isInvoiceOverdue } from '@/utils/invoiceHelpers'
import { SUPPLIER_STATE } from '@/utils/gstStates'
import numberToWords from 'number-to-words'

const statusConfig: Record<string, { label: string; color: string; bg: string }> = {
  draft:          { label: 'Draft',          color: '#8A93A6', bg: '#F1F4F9' },
  submitted:      { label: 'Submitted',      color: '#D97706', bg: '#FEF3C7' },
  rejected:       { label: 'Rejected',       color: '#DC2626', bg: '#FEE2E2' },
  issued:         { label: 'Issued',         color: '#2563EB', bg: '#EFF4FF' },
  partially_paid: { label: 'Partially Paid', color: '#92400E', bg: '#FEF3C7' },
  paid:           { label: 'Paid',           color: '#065F46', bg: '#D1FAE5' },
  overdue:        { label: 'Overdue',        color: '#991B1B', bg: '#FEE2E2' },
  cancelled:      { label: 'Cancelled',      color: '#6B7280', bg: '#F3F4F6' },
}

const fmt = (n: number) => `₹${(Number(n) || 0).toLocaleString('en-IN')}`

const TickIcon = ({ v, label }: { v: boolean; label?: string }) => (
  <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
    {v
      ? <CheckCircleOutlined style={{ color: '#059669', fontSize: 16 }} />
      : <CloseCircleOutlined style={{ color: '#DC2626', fontSize: 16 }} />
    }
    {label && <span style={{ fontSize: 13, fontWeight: 600, color: v ? '#059669' : '#DC2626' }}>{label}</span>}
  </span>
)

const InvoiceDetail: React.FC = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { consumers, generators, bankAccounts } = useMasterData()
  const defaultBank = bankAccounts.find(b => b.isDefault && b.status === 'active') || bankAccounts.find(b => b.status === 'active')
  const { canEmail, canDownload } = useModulePermission('Finance')
  const [correctionModal, setCorrectionModal] = useState(false)
  const [emailingInvoice, setEmailingInvoice] = useState(false)
  const [corrForm] = Form.useForm()
  const [, setTick] = useState(0)

  // Pull the latest invoice + payment records so this page reflects the DB.
  useEffect(() => {
    Promise.all([invoiceStore.refresh(), paymentStore.refresh()]).then(() => setTick(t => t + 1))
  }, [id])

  // ── Load the real invoice (no demo fallback) ──────────
  const rec = id ? invoiceStore.get(id) : undefined
  if (!rec) {
    // Don't claim "not found" before invoiceStore has actually finished its
    // first fetch — on a fresh page load (or a hard refresh of this exact
    // URL) the store starts empty, so this branch was hit immediately and
    // showed a false "not found" that only corrected itself once refresh()
    // (kicked off above) resolved a moment later. Show a loading placeholder
    // instead until `loaded` is genuinely true.
    if (!invoiceStore.loaded) {
      return (
        <div style={{ padding: 24 }}>
          <Skeleton active paragraph={{ rows: 2 }} style={{ marginBottom: 24 }} />
          <Skeleton active paragraph={{ rows: 8 }} />
        </div>
      )
    }
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Empty description="Invoice not found" />
        <Button type="primary" style={{ marginTop: 16 }} onClick={() => navigate('/invoices')}>Back to Invoices</Button>
      </div>
    )
  }
  const entityRec: any = rec.type === 'generator'
    ? generators.find(g => g.id === rec.entityId)
    : consumers.find(c => c.id === rec.entityId)

  // rec's amount/rate columns are Postgres `numeric` and arrive as strings
  // (e.g. "1000.00") — coerce every one of them here, once, before any
  // arithmetic or display formatting touches them. `+` would otherwise
  // concatenate instead of add, and a "0.00" string is truthy so `rec.x ?`
  // checks below would take the wrong branch on a genuinely-zero amount.
  const baseAmount  = Number(rec.baseAmount) || 0
  const gstAmount   = Number(rec.gstAmount) || 0
  const totalAmount = Number(rec.totalAmount) || 0
  const paidAmount  = Number(rec.paidAmount) || 0
  const igstAmount  = Number(rec.igstAmount) || 0
  const isIgst      = igstAmount > 0
  const cgstAmount  = rec.cgstAmount != null ? Number(rec.cgstAmount) : (isIgst ? 0 : gstAmount / 2)
  const sgstAmount  = rec.sgstAmount != null ? Number(rec.sgstAmount) : (isIgst ? 0 : gstAmount / 2)
  const cgstRate    = baseAmount ? Math.round((cgstAmount / baseAmount) * 100) : 9
  const sgstRate    = baseAmount ? Math.round((sgstAmount / baseAmount) * 100) : 9
  const igstRate    = baseAmount ? Math.round((igstAmount / baseAmount) * 100) : 18
  const units       = Number(rec.units) || 0
  const tariff      = rec.tariff != null ? Number(rec.tariff) : (units ? baseAmount / units : 0)

  const clientAddress = rec.clientAddress?.length
    ? rec.clientAddress
    : String(entityRec?.companyAddress || '').split(',').map((s: string) => s.trim()).filter(Boolean)

  const inv = {
    ...rec,
    entityId: rec.entityId || '—',
    allocationId: rec.allocationId || '—',
    baseAmount, gstAmount, totalAmount, paidAmount,
    units, tariff,
    cgstRate, sgstRate, cgstAmount, sgstAmount, igstAmount, igstRate, isIgst,
    hsn: rec.sac || '998312',
    sac: rec.sac || '998312',
    placeOfSupply: rec.placeOfSupply || 'Tamil Nadu (33)',
    reverseCharge: false,
    notes: rec.notes || '',
    clientName: entityRec?.name || rec.entity,
    clientAddress,
    clientGstNo: rec.clientGstNo || entityRec?.gstin || '',
    customerRef: rec.customerRef || rec.entityId || '',
    payments: paidAmount > 0
      ? [{ key: '1', date: rec.issueDate, mode: 'NEFT', reference: `RCPT-${rec.id}`, amount: paidAmount, tds: 0 }]
      : [] as { key: string; date: string; mode: string; reference: string; amount: number; tds: number }[],
  }

  const cfg = statusConfig[inv.status]
  const outstanding = inv.totalAmount - inv.paidAmount

  // Real receipts recorded against THIS invoice (falls back to the derived
  // single row for legacy invoices that only carry a paidAmount).
  const storePayments = paymentStore.getAll()
    .filter(p => Array.isArray(p.invoices) && p.invoices.some(a => a.invoiceId === inv.id || a.invoiceNumber === inv.invoiceNumber))
    .map(p => ({ key: p.id, date: p.date, mode: p.mode, reference: p.receiptNumber, amount: Number(p.amount) || 0, tds: Number(p.tds) || 0 }))
  const paymentRows = storePayments.length ? storePayments : inv.payments
  const totalReceived = storePayments.length
    ? storePayments.reduce((s, r) => s + r.amount, 0)
    : inv.paidAmount

  const handleCorrection = () => {
    corrForm.validateFields().then(values => {
      const corrTypeLabel: Record<string, string> = {
        credit: 'Credit Note — Reduce invoice amount',
        debit: 'Debit Note — Increase invoice amount',
        data: 'Data Correction — Entity / period / units',
        gst: 'GST Correction — HSN / rate / place of supply',
      }
      const entry = `[Correction requested ${new Date().toISOString().slice(0, 10)}] ${corrTypeLabel[values.corrType] || values.corrType}: ${values.description}${values.doc ? ` (doc: ${values.doc})` : ''}`
      invoiceStore.save({ id: inv.id, notes: rec.notes ? `${rec.notes}\n${entry}` : entry })
      message.success('Correction request recorded on the invoice. Note: there is no automated re-approval workflow yet — follow up with your approver directly.')
      setCorrectionModal(false)
      corrForm.resetFields()
      setTick(t => t + 1)
    }).catch(() => { /* validation errors shown inline */ })
  }

  // Item 22: Record Payment available ONLY when Invoice status is Approved/Issued/Partially Paid/Overdue AND has outstanding balance
  const canRecordPayment = ['issued', 'approved', 'partially_paid', 'overdue'].includes(inv.status) && outstanding > 0

  // Item 24: Export GSTR Filing CSV data
  const handleExportGstr = () => {
    const csvHeader = 'GSTIN/UIN of Recipient,Receiver Name,Invoice Number,Invoice Date,Invoice Value,Place Of Supply,Reverse Charge,Applicable % of Tax Rate,Taxable Value,Cess Amount,HSN Code,CGST Amount,SGST Amount,IGST Amount'
    const csvRow = [
      `"${inv.clientGstNo || 'URP'}"`,
      `"${(inv.entity || '').replace(/"/g, '""')}"`,
      `"${inv.invoiceNumber}"`,
      `"${inv.issueDate}"`,
      inv.totalAmount,
      `"${inv.placeOfSupply || SUPPLIER_STATE}"`,
      inv.reverseCharge ? 'Y' : 'N',
      inv.igstAmount && inv.igstAmount > 0 ? 18 : 18,
      inv.baseAmount,
      0,
      `"${inv.sac || '998312'}"`,
      inv.cgstAmount || 0,
      inv.sgstAmount || 0,
      inv.igstAmount || 0
    ].join(',')

    const blob = new Blob([`${csvHeader}\n${csvRow}`], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.download = `GSTR1_Export_${inv.invoiceNumber}.csv`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    message.success(`GSTR-1 data for invoice ${inv.invoiceNumber} exported successfully!`)
  }

  const preparePdfData = () => {
    // Subtotal (base + tax), then round to the nearest rupee like the sample invoice
    const subtotal   = inv.baseAmount + inv.gstAmount
    const grandTotal = Math.round(subtotal)
    const roundedOff = +(grandTotal - subtotal).toFixed(2)

    // Amount in words (on the rounded total)
    const amountWords = numberToWords.toWords(grandTotal).replace(/-/g, ' ')
    const capitalizedWords = amountWords.charAt(0).toUpperCase() + amountWords.slice(1) + ' Indian Rupees'

    const useIgst = inv.igstAmount > 0

    return {
      invoiceNumber: inv.invoiceNumber,
      issueDate: inv.issueDate,
      dueDate: inv.dueDate,
      // customerRef is the entity's own HT/HTSC number (see InvoiceForm.tsx
      // and AllocationList.tsx's auto-generator) — a plain value, no label
      // prefix. Only fall back to the internal entityId when it's genuinely
      // missing, and don't dress that internal id up as an "HTSC No" it isn't.
      customerId: inv.customerRef || inv.entityId,
      poNumber: `${inv.period} Billing Cycle`,
      clientName: inv.clientName,
      clientAddress: inv.clientAddress,
      clientGstNo: inv.clientGstNo,
      // Same text shown as "Notes" in the Invoice Info panel above — keeps the
      // downloaded/printed PDF consistent with what's on screen instead of a
      // generic line that doesn't say what the invoice actually covers. The
      // internal allocation id is stripped — it's bookkeeping, not something
      // the invoice recipient needs to see.
      description: inv.notes ? stripAllocationRef(inv.notes) : `Service Charges For the month Of ${inv.period}`,
      hsnSacCode: inv.sac,
      quantity: inv.units,
      ratePerUnit: inv.tariff,
      lineTotal: inv.baseAmount,
      cgstRate: useIgst ? 0 : inv.cgstRate,
      cgstAmount: useIgst ? 0 : inv.cgstAmount,
      sgstRate: useIgst ? 0 : inv.sgstRate,
      sgstAmount: useIgst ? 0 : inv.sgstAmount,
      igstRate: useIgst ? (inv.cgstRate + inv.sgstRate) : 0,
      igstAmount: useIgst ? inv.gstAmount : 0,
      subtotal,
      roundedOff,
      discount: 0,
      totalAmount: grandTotal,
      amountInWords: capitalizedWords,
      bankName: defaultBank?.bankName,
      bankBranch: defaultBank?.branchName,
      bankAccountNumber: defaultBank?.accountNumber,
      bankIfscCode: defaultBank?.ifscCode,
      bankSwiftCode: defaultBank?.swiftCode,
    }
  }

  const handleDownloadPDF = () => {
    const data = preparePdfData()
    downloadInvoiceAsPDF(data, `${inv.invoiceNumber}.pdf`)
  }

  const handlePrint = () => {
    const data = preparePdfData()
    const htmlContent = generateInvoiceHTML(data)
    printInvoicePDF(htmlContent)
  }

  const paymentColumns = [
    { title: 'Date', dataIndex: 'date', render: (d: string) => <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 12 }}>{d}</span> },
    { title: 'Mode', dataIndex: 'mode', render: (m: string) => <Tag style={{ borderRadius: 100 }}>{m}</Tag> },
    { title: 'Reference / Receipt No.', dataIndex: 'reference', render: (r: string) => <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 12, color: '#4A5568', fontWeight: 600 }}>{r}</span> },
    { title: 'Amount Received', dataIndex: 'amount', render: (a: number) => <span style={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 700, color: '#059669' }}>{fmt(a)}</span> },
    { title: 'TDS Deducted', dataIndex: 'tds', render: (t: number) => <span style={{ fontFamily: 'JetBrains Mono, monospace' }}>{t > 0 ? fmt(t) : '—'}</span> },
    {
      title: 'Receipt',
      key: 'receipt',
      render: (_: any, r: any) => {
        const pRecord = paymentStore.getAll().find(p => p.receiptNumber === r.reference || p.id === r.key)
        const payObject = pRecord || {
          id: r.key || `RCP-${Date.now()}`,
          receiptNumber: r.reference || `RCP-${inv.invoiceNumber}`,
          date: r.date,
          customer: inv.clientName,
          customerId: rec.entityId || '',
          invoices: [{ invoiceId: inv.id, invoiceNumber: inv.invoiceNumber, applied: r.amount + r.tds }],
          baseAmount: Math.max(0, r.amount - inv.gstAmount),
          gstAmount: inv.gstAmount,
          amount: r.amount + r.tds,
          tds: r.tds,
          amountReceived: r.amount,
          mode: r.mode,
          status: 'confirmed',
          paymentStatus: 'paid',
        }
        return (
          <Space wrap>
            <Tooltip title="Print Payment Receipt">
              <Button size="small" icon={<PrinterOutlined />} onClick={() => printReceipt(payObject as any)}>Receipt</Button>
            </Tooltip>
            <Tooltip title="Download Receipt PDF">
              <Button size="small" icon={<FilePdfOutlined />} onClick={() => downloadReceiptAsPDF(payObject as any)} />
            </Tooltip>
          </Space>
        )
      }
    }
  ]

  const tabItems = [
    {
      key: 'details',
      label: 'Invoice Details',
      children: (
        <div>
          <WorkflowChainDisplay moduleName="Invoice" />
          <Row gutter={[20, 20]}>
            {/* Left: Invoice info */}
            <Col xs={24} lg={12}>
              <div className="panel" style={{ height: '100%' }}>
                <div className="panel-header">
                  <span className="panel-header-title"><FileTextOutlined style={{ color: '#001A4D' }} /> Invoice Info</span>
                </div>
                <div className="panel-body">
                  <Descriptions column={1} size="small" styles={{ label: { width: 160 } }}>
                    <Descriptions.Item label="Invoice Number">
                      <span style={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 700 }}>{inv.invoiceNumber}</span>
                    </Descriptions.Item>
                    <Descriptions.Item label="Type">
                      <span className="pill pill-brand" style={{ background: inv.type === 'generator' ? 'rgba(0,26,77,0.08)' : '#EFF4FF', color: inv.type === 'generator' ? '#001A4D' : '#2563EB' }}>
                        {inv.type === 'generator' ? 'Generator Invoice' : 'Consumer Invoice'}
                      </span>
                    </Descriptions.Item>
                    <Descriptions.Item label="Entity">{inv.entity} ({inv.entityId})</Descriptions.Item>
                    <Descriptions.Item label="Billing Period">{inv.period}</Descriptions.Item>
                    <Descriptions.Item label="Allocation ID">
                      <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 12 }}>{inv.allocationId}</span>
                    </Descriptions.Item>
                    <Descriptions.Item label="Issue Date"><span style={{ fontFamily: 'JetBrains Mono, monospace' }}>{inv.issueDate}</span></Descriptions.Item>
                    <Descriptions.Item label="Due Date"><span style={{ fontFamily: 'JetBrains Mono, monospace' }}>{inv.dueDate}</span></Descriptions.Item>
                    {inv.notes && <Descriptions.Item label="Notes"><em style={{ color: '#4A5568' }}>{inv.notes}</em></Descriptions.Item>}
                  </Descriptions>
                </div>
              </div>
            </Col>

            {/* Right: Amount breakup */}
            <Col xs={24} lg={12}>
              <div className="panel" style={{ height: '100%' }}>
                <div className="panel-header">
                  <span className="panel-header-title">Amount Breakup</span>
                </div>
                <div className="panel-body">
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {[
                      { label: 'Units Supplied', value: `${inv.units.toLocaleString('en-IN')} kWh`, mono: true },
                      { label: 'Tariff Rate',    value: `₹${inv.tariff}/kWh`, mono: true },
                    ].map(r => (
                      <div key={r.label} style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ fontSize: 13, color: '#4A5568' }}>{r.label}</span>
                        <span style={{ fontFamily: r.mono ? 'JetBrains Mono, monospace' : 'inherit', fontSize: 13, fontWeight: 600 }}>{r.value}</span>
                      </div>
                    ))}
                    <Divider style={{ margin: '4px 0' }} />
                    {[
                      { label: 'Base Amount',    value: fmt(inv.baseAmount) },
                      ...(inv.isIgst
                        ? [{ label: `IGST @ ${inv.igstRate}%`, value: fmt(inv.igstAmount) }]
                        : [
                            { label: `CGST @ ${inv.cgstRate}%`, value: fmt(inv.cgstAmount) },
                            { label: `SGST @ ${inv.sgstRate}%`, value: fmt(inv.sgstAmount) },
                          ]),
                    ].map(r => (
                      <div key={r.label} style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ fontSize: 13, color: '#4A5568' }}>{r.label}</span>
                        <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 13 }}>{r.value}</span>
                      </div>
                    ))}
                    <Divider style={{ margin: '4px 0' }} />
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontWeight: 700, fontSize: 14 }}>Total Amount</span>
                      <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 20, fontWeight: 700, color: '#0B1426' }}>{fmt(inv.totalAmount)}</span>
                    </div>
                    {outstanding > 0 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', background: '#FEF3C7', borderRadius: 8, border: '1px solid #FDE68A' }}>
                        <span style={{ fontWeight: 600, fontSize: 13, color: '#92400E' }}>Outstanding</span>
                        <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 16, fontWeight: 700, color: '#92400E' }}>{fmt(outstanding)}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </div>
      ),
    },
    {
      key: 'gst',
      label: 'GST Compliance',
      children: (
        <div>
          <Row gutter={[20, 20]}>
            <Col xs={24} lg={14}>
              <div className="panel">
                <div className="panel-header">
                  <span className="panel-header-title">GST Details</span>
                </div>
                <div className="panel-body">
                  <Descriptions column={2} size="small">
                    <Descriptions.Item label="HSN Code"><span style={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 600 }}>{inv.hsn}</span></Descriptions.Item>
                    <Descriptions.Item label="SAC Code"><span style={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 600 }}>{inv.sac}</span></Descriptions.Item>
                    <Descriptions.Item label="Place of Supply" span={2}>{inv.placeOfSupply}</Descriptions.Item>
                    <Descriptions.Item label="Reverse Charge">
                      <TickIcon v={inv.reverseCharge} label={inv.reverseCharge ? 'Applicable' : 'Not Applicable'} />
                    </Descriptions.Item>
                  </Descriptions>
                  <Divider />
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                    <div style={{ padding: '14px 16px', background: inv.irn ? '#D1FAE5' : '#FEE2E2', borderRadius: 10, border: `1px solid ${inv.irn ? '#6EE7B7' : '#FECACA'}` }}>
                      <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: inv.irn ? '#065F46' : '#991B1B', marginBottom: 6 }}>e-Invoice IRN</div>
                      {inv.irn
                        ? <>
                            <div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 12, color: '#059669', wordBreak: 'break-all', marginBottom: 4 }}>{inv.irn}</div>
                            <Tooltip title="Copy IRN">
                              <Button size="small" icon={<CopyOutlined />} type="link" style={{ padding: 0, height: 'auto', color: '#059669' }}>
                                Copy
                              </Button>
                            </Tooltip>
                          </>
                        : <div style={{ color: '#DC2626', fontSize: 13, fontWeight: 600 }}>Not Generated</div>
                      }
                    </div>
                    <div style={{ padding: '14px 16px', background: inv.ewayBill ? '#D1FAE5' : '#F1F4F9', borderRadius: 10, border: `1px solid ${inv.ewayBill ? '#6EE7B7' : '#E4E8F0'}` }}>
                      <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: inv.ewayBill ? '#065F46' : '#8A93A6', marginBottom: 6 }}>e-Way Bill</div>
                      {inv.ewayBill
                        ? <div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 12, color: '#059669', wordBreak: 'break-all' }}>{inv.ewayBill}</div>
                        : <div style={{ color: '#8A93A6', fontSize: 13 }}>Not Applicable</div>
                      }
                    </div>
                  </div>
                </div>
              </div>
            </Col>
            <Col xs={24} lg={10}>
              <div className="panel">
                <div className="panel-header">
                  <span className="panel-header-title">Return Filing Status</span>
                </div>
                <div className="panel-body">
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                    {[
                      { label: 'GSTR-1', desc: 'Outward Supply Return', filed: inv.gstr1Filed },
                      { label: 'GSTR-3B', desc: 'Monthly Summary Return', filed: inv.gstr3bFiled },
                    ].map(r => (
                      <div key={r.label} style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '12px 16px',
                        background: r.filed ? '#D1FAE5' : '#FEF3C7',
                        borderRadius: 10,
                        border: `1px solid ${r.filed ? '#6EE7B7' : '#FDE68A'}`,
                      }}>
                        <div>
                          <div style={{ fontWeight: 700, fontSize: 14, color: r.filed ? '#065F46' : '#92400E' }}>{r.label}</div>
                          <div style={{ fontSize: 12, color: r.filed ? '#059669' : '#B45309' }}>{r.desc}</div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 700, color: r.filed ? '#059669' : '#92400E' }}>
                          {r.filed ? <CheckCircleOutlined /> : <ExclamationCircleOutlined />}
                          {r.filed ? 'Filed' : 'Pending'}
                        </div>
                      </div>
                    ))}
                    <Button block type={(!inv.gstr1Filed || !inv.gstr3bFiled) ? 'primary' : 'default'} style={{ marginTop: 4 }} onClick={handleExportGstr}>
                      Export for GSTR Filing
                    </Button>
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </div>
      ),
    },
    {
      key: 'payments',
      label: `Payment History (${paymentRows.length})`,
      children: (
        <div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
            <Button
              type="primary"
              icon={<DollarOutlined />}
              onClick={() => navigate('/payments/new', { state: { invoiceId: inv.id } })}
              disabled={!canRecordPayment}
            >
              Record Payment
            </Button>
          </div>
          {paymentRows.length === 0
            ? <div style={{ textAlign: 'center', padding: '48px 0', color: '#8A93A6' }}>No payments recorded yet.</div>
            : <>
                <div style={{ marginBottom: 16, padding: '12px 16px', background: '#D1FAE5', borderRadius: 10, border: '1px solid #6EE7B7', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontWeight: 600, color: '#065F46' }}>Total Received</span>
                  <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 18, fontWeight: 700, color: '#059669' }}>{fmt(totalReceived)}</span>
                </div>
                <Table dataSource={paymentRows} columns={paymentColumns} pagination={false} />
              </>
          }
        </div>
      ),
    },
  ]

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 20 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/invoices')} />
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <h2 className="page-title">{inv.invoiceNumber}</h2>
            <span className="pill" style={{ background: cfg.bg, color: cfg.color }}>{cfg.label}</span>
            <span className="pill" style={{ background: inv.type === 'generator' ? 'rgba(0,26,77,0.08)' : '#EFF4FF', color: inv.type === 'generator' ? '#001A4D' : '#2563EB' }}>
              {inv.type === 'generator' ? 'Generator Invoice' : 'Consumer Invoice'}
            </span>
          </div>
          <p className="page-subtitle">{inv.entity} · {inv.period}</p>
        </div>
        <Space wrap>
          {canRecordPayment && (
            <Button
              icon={<DollarOutlined />}
              onClick={() => navigate('/payments/new', { state: { invoiceId: inv.id } })}
              style={{ background: '#059669', borderColor: '#059669', color: '#fff' }}
            >
              Record Payment
            </Button>
          )}
          {canEmail && (
            <Button
              icon={<MailOutlined />}
              loading={emailingInvoice}
              onClick={async () => {
                setEmailingInvoice(true)
                try {
                  const sentTo = await emailInvoice(inv.id, preparePdfData())
                  message.success(`Invoice emailed to ${sentTo}`)
                } catch (err) {
                  message.error(err instanceof Error ? err.message : 'Could not email invoice')
                } finally {
                  setEmailingInvoice(false)
                }
              }}
            >
              Email
            </Button>
          )}
          <Button icon={<PrinterOutlined />} onClick={handlePrint}>Print</Button>
          {canDownload && (
            <Button
              type="primary"
              icon={<FilePdfOutlined />}
              onClick={handleDownloadPDF}
              style={{
                background: '#2563EB',
                borderColor: '#2563EB',
                color: '#FFFFFF',
                fontWeight: 600,
                boxShadow: '0 2px 4px rgba(37, 99, 235, 0.2)',
              }}
            >
              Download PDF
            </Button>
          )}
          <Button
            icon={<ExclamationCircleOutlined />}
            onClick={() => setCorrectionModal(true)}
            disabled={inv.status === 'paid'}
          >
            Request Correction
          </Button>
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={() => navigate(`/invoices/${inv.id}/edit`)}
            disabled={inv.status === 'paid'}
          >
            Edit
          </Button>
        </Space>
      </div>

      {/* KPI row */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 24, flexWrap: 'wrap' }}>
        <div className="stat-card" style={{ flex: 1, minWidth: 140 }}>
          <div className="stat-label">Base Amount</div>
          <div className="stat-value" style={{ fontSize: 18 }}>{fmt(inv.baseAmount)}</div>
          <div className="stat-meta">{inv.units.toLocaleString('en-IN')} kWh × ₹{inv.tariff}</div>
        </div>
        <div className="stat-card" style={{ flex: 1, minWidth: 140, borderLeftColor: '#2563EB' }}>
          <div className="stat-label">GST Amount</div>
          <div className="stat-value" style={{ fontSize: 18 }}>{fmt(inv.gstAmount)}</div>
          <div className="stat-meta">{inv.isIgst ? `IGST ${inv.igstRate}%` : `CGST ${inv.cgstRate}% + SGST ${inv.sgstRate}%`}</div>
        </div>
        <div className="stat-card" style={{ flex: 1, minWidth: 140 }}>
          <div className="stat-label">Total Amount</div>
          <div className="stat-value" style={{ fontSize: 18 }}>{fmt(inv.totalAmount)}</div>
          <div className="stat-meta">Invoice value</div>
        </div>
        <div className="stat-card green" style={{ flex: 1, minWidth: 140 }}>
          <div className="stat-label">Collected</div>
          <div className="stat-value" style={{ fontSize: 18, color: '#059669' }}>{fmt(inv.paidAmount)}</div>
          <div className="stat-meta">{outstanding > 0 ? `₹${outstanding.toLocaleString('en-IN')} pending` : 'Fully paid'}</div>
        </div>
      </div>

      {isInvoiceOverdue(inv) && (
        <Alert
          type="error"
          showIcon
          message="This invoice is overdue"
          description={`Payment was due on ${inv.dueDate}. Outstanding: ${fmt(outstanding)}`}
          style={{ marginBottom: 20, borderRadius: 10 }}
        />
      )}

      {/* Tabs */}
      <Card styles={{ body: { padding: '0 20px 20px' } }}>
        <Tabs items={tabItems} defaultActiveKey="details" />
      </Card>

      {/* ── Request Correction Modal (IN-03) ──────────────── */}
      <Modal
        open={correctionModal}
        onCancel={() => setCorrectionModal(false)}
        onOk={handleCorrection}
        title="Request Invoice Correction"
        okText="Submit for Re-approval"
        width={540}
      >
        <Alert
          type="warning"
          showIcon
          message="Re-approval workflow required (IN-03)"
          description="Invoice corrections go through a re-approval workflow. A Credit/Debit Note will be issued where required by GST. The original invoice remains unchanged until the corrected version is approved."
          style={{ marginBottom: 20, borderRadius: 8 }}
        />
        <Form form={corrForm} layout="vertical">
          <Form.Item label="Correction Type" name="corrType" rules={[{ required: true }]}>
            <Select
              options={[
                { label: 'Credit Note — Reduce invoice amount', value: 'credit' },
                { label: 'Debit Note — Increase invoice amount', value: 'debit' },
                { label: 'Data Correction — Entity / period / units', value: 'data' },
                { label: 'GST Correction — HSN / rate / place of supply', value: 'gst' },
              ]}
              placeholder="Select correction type"
            />
          </Form.Item>
          <Form.Item label="Description of Correction" name="description" rules={[{ required: true }]}>
            <Input.TextArea rows={3} placeholder="Describe what needs to be corrected and why…" />
          </Form.Item>
          <Form.Item label="Supporting Document (optional)" name="doc">
            <Input placeholder="Document reference or upload path" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}

export default InvoiceDetail
